import React, { useState, useEffect } from 'react';
import { FaTimes, FaMapMarkerAlt, FaPlus, FaSearch, FaToggleOn, FaToggleOff, FaEdit, FaTrash } from 'react-icons/fa';
import axiosInstance from '../../../libs/axios';

interface State {
  id: number;
  name: string;
}

interface District {
    id: number;
    name: string;
    is_active: boolean;
    state_circle_id: number;
}

interface Pagination {
    offset: number;
    limit: number;
    page: number;
    total_pages: number;
    total_count: number;
    has_next_page: boolean;
    has_prev_page: boolean;
    next_page: number | null;
    prev_page: number | null;
}

interface DistrictsResponse {
    districts: District[];
    pagination: Pagination;
}

interface AddDistrictOffcanvasProps {
    isOpen: boolean;
    onClose: () => void;
    state: State;
}

export const AddDistrictOffcanvas: React.FC<AddDistrictOffcanvasProps> = ({
    isOpen,
    onClose,
    state
}) => {
    const [districts, setDistricts] = useState<District[]>([]);
    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newDistrictName, setNewDistrictName] = useState('');
    const [addingDistrict, setAddingDistrict] = useState(false);
    const [addError, setAddError] = useState('');
    
    const [showEditForm, setShowEditForm] = useState(false);
    const [editingDistrictId, setEditingDistrictId] = useState<number | null>(null);
    const [editDistrictName, setEditDistrictName] = useState('');
    const [editingDistrict, setEditingDistrict] = useState(false);
    const [editError, setEditError] = useState('');
    
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingDistrictId, setDeletingDistrictId] = useState<number | null>(null);
    const [deletingDistrict, setDeletingDistrict] = useState(false);

    // Fetch districts
    const fetchDistricts = async (page: number = 1, search: string = '') => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(
                `/states/${state.id}/districts?search=${search}&page=${page}&limit=10`
            );
            const data: DistrictsResponse = response.data.data;
            setDistricts(data.districts);
            setPagination(data.pagination);
        } catch (error) {
            console.error('Failed to fetch districts:', error);
            setDistricts([]);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    };

    // Reset and fetch on open
    useEffect(() => {
        if (isOpen && state) {
            setSearchTerm('');
            setCurrentPage(1);
            setShowAddForm(false);
            setNewDistrictName('');
            setAddError('');
            setShowEditForm(false);
            setEditingDistrictId(null);
            setEditDistrictName('');
            setEditError('');
            setShowDeleteConfirm(false);
            setDeletingDistrictId(null);
            fetchDistricts(1, '');
        }
    }, [isOpen, state]);

    // Handle search
    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
        fetchDistricts(1, value);
    };

    // Handle pagination
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchDistricts(page, searchTerm);
    };

    // Add new district
    const handleAddDistrict = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!newDistrictName.trim()) {
            setAddError('District name is required');
            return;
        }

        if (newDistrictName.trim().length < 2) {
            setAddError('District name must be at least 2 characters long');
            return;
        }

        setAddingDistrict(true);
        setAddError('');

        try {
            await axiosInstance.post('/states/districts', {
                state_id: state.id,
                name: newDistrictName.trim()
            });

            // Reset form and refresh list
            setNewDistrictName('');
            setShowAddForm(false);
            setCurrentPage(1);
            setSearchTerm('');
            fetchDistricts(1, '');
        } catch (error: any) {
            setAddError(error.response?.data?.message || 'Failed to add district');
        } finally {
            setAddingDistrict(false);
        }
    };

    // Toggle district status
    const handleToggleStatus = async (district: District) => {
        try {
            await axiosInstance.patch(`/states/districts/${district.id}/status`, { is_active: !district.is_active });
            fetchDistricts(currentPage, searchTerm);
        } catch (error) {
            console.error('Failed to toggle district status:', error);
        }
    };

    // Open edit form with prefilled data
    const handleOpenEdit = (district: District) => {
        setEditingDistrictId(district.id);
        setEditDistrictName(district.name);
        setEditError('');
        setShowEditForm(true);
        setShowAddForm(false);
    };

    // Update district
    const handleUpdateDistrict = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!editDistrictName.trim()) {
            setEditError('District name is required');
            return;
        }

        if (editDistrictName.trim().length < 2) {
            setEditError('District name must be at least 2 characters long');
            return;
        }

        setEditingDistrict(true);
        setEditError('');

        try {
            await axiosInstance.put(`/states/districts/${editingDistrictId}`, {
                name: editDistrictName.trim()
            });

            setEditDistrictName('');
            setEditingDistrictId(null);
            setShowEditForm(false);
            setCurrentPage(1);
            setSearchTerm('');
            fetchDistricts(1, '');
        } catch (error: any) {
            setEditError(error.response?.data?.message || 'Failed to update district');
        } finally {
            setEditingDistrict(false);
        }
    };

    // Open delete confirmation
    const handleOpenDelete = (districtId: number) => {
        setDeletingDistrictId(districtId);
        setShowDeleteConfirm(true);
    };

    // Delete district
    const handleDeleteDistrict = async () => {
        if (!deletingDistrictId) return;

        setDeletingDistrict(true);

        try {
            await axiosInstance.delete(`/states/districts/${deletingDistrictId}`);
            
            setShowDeleteConfirm(false);
            setDeletingDistrictId(null);
            setCurrentPage(1);
            setSearchTerm('');
            fetchDistricts(1, '');
        } catch (error: any) {
            console.error('Failed to delete district:', error);
            setShowDeleteConfirm(false);
            setDeletingDistrictId(null);
        } finally {
            setDeletingDistrict(false);
        }
    };

    const handleClose = () => {
        if (!addingDistrict && !editingDistrict && !deletingDistrict) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={handleClose} />

            {/* Offcanvas */}
            <div className="overflow-y-auto fixed right-0 top-0 h-full w-full sm:max-w-4xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-800">
                            Manage Districts - {state.name}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">Add and manage districts for this state.</p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        disabled={addingDistrict}
                    >
                        <FaTimes className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-6">
                        {/* Add District Form */}
                        {showAddForm && (
                            <div className="mb-6 p-4 border border-gray-200 rounded bg-gray-50">
                                <form onSubmit={handleAddDistrict} className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-800">Add New District</h3>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            District Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <FaMapMarkerAlt className="w-4 h-4 text-gray-400" />
                                            </div>
                                            <input
                                                type="text"
                                                value={newDistrictName}
                                                onChange={(e) => {
                                                    setNewDistrictName(e.target.value);
                                                    if (addError) setAddError('');
                                                }}
                                                placeholder="Enter district name"
                                                className={`block w-full pl-10 pr-3 py-2 border rounded shadow-sm focus:ring-2 focus:ring-blue-700 focus:border-blue-700 text-sm ${
                                                    addError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                                disabled={addingDistrict}
                                            />
                                        </div>
                                        {addError && (
                                            <p className="mt-1 text-sm text-red-600">{addError}</p>
                                        )}
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            type="submit"
                                            disabled={addingDistrict || !newDistrictName.trim()}
                                            className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-900 focus:ring-2 focus:ring-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                        >
                                            {addingDistrict ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                    Adding...
                                                </>
                                            ) : (
                                                <>
                                                    <FaPlus className="w-4 h-4" />
                                                    Add District
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowAddForm(false);
                                                setNewDistrictName('');
                                                setAddError('');
                                            }}
                                            disabled={addingDistrict}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-700 disabled:opacity-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Edit District Form */}
                        {showEditForm && (
                            <div className="mb-6 p-4 border border-blue-200 rounded bg-blue-50">
                                <form onSubmit={handleUpdateDistrict} className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-800">Edit District</h3>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            District Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <FaMapMarkerAlt className="w-4 h-4 text-gray-400" />
                                            </div>
                                            <input
                                                type="text"
                                                value={editDistrictName}
                                                onChange={(e) => {
                                                    setEditDistrictName(e.target.value);
                                                    if (editError) setEditError('');
                                                }}
                                                placeholder="Enter district name"
                                                className={`block w-full pl-10 pr-3 py-2 border rounded shadow-sm focus:ring-2 focus:ring-blue-700 focus:border-blue-700 text-sm ${
                                                    editError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                                disabled={editingDistrict}
                                            />
                                        </div>
                                        {editError && (
                                            <p className="mt-1 text-sm text-red-600">{editError}</p>
                                        )}
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            type="submit"
                                            disabled={editingDistrict || !editDistrictName.trim()}
                                            className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-900 focus:ring-2 focus:ring-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                        >
                                            {editingDistrict ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                    Updating...
                                                </>
                                            ) : (
                                                <>
                                                    <FaEdit className="w-4 h-4" />
                                                    Update District
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowEditForm(false);
                                                setEditingDistrictId(null);
                                                setEditDistrictName('');
                                                setEditError('');
                                            }}
                                            disabled={editingDistrict}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-700 disabled:opacity-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Controls */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => setShowAddForm(!showAddForm)}
                                    disabled={addingDistrict}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-900 focus:ring-2 focus:ring-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                                >
                                    <FaPlus className="w-4 h-4" />
                                    Add District
                                </button>
                                
                                {pagination && (
                                    <span className="text-sm text-gray-600">
                                        Total: {pagination.total_count} districts
                                    </span>
                                )}
                            </div>

                            {/* Search */}
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaSearch className="w-4 h-4 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    placeholder="Search districts..."
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded  shadow-sm focus:ring-2 focus:ring-blue-700 focus:border-blue-700 text-sm"
                                />
                            </div>
                        </div>

                        {/* Districts List */}
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
                                <span className="ml-2 text-gray-600">Loading districts...</span>
                            </div>
                        ) : districts.length === 0 ? (
                            <div className="text-center py-12">
                                <FaMapMarkerAlt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No Districts Found</h3>
                                <p className="text-gray-600 mb-4">
                                    {searchTerm ? 
                                        `No districts match "${searchTerm}"` : 
                                        'No districts added to this state yet.'
                                    }
                                </p>
                                {!searchTerm && (
                                    <button
                                        onClick={() => setShowAddForm(true)}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-900 focus:ring-2 focus:ring-blue-700 transition-colors flex items-center gap-2 mx-auto"
                                    >
                                        <FaPlus className="w-4 h-4" />
                                        Add First District
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {districts.map((district) => (
                                    <div key={district.id} className="flex items-center justify-between p-4 border border-gray-200 rounded bg-white hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-3 flex-1">
                                            <div className="flex-shrink-0">
                                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                    <FaMapMarkerAlt className="w-4 h-4 text-blue-800" />
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="text-sm font-medium text-gray-900">{district.name}</h4>
                                                <p className="text-xs text-gray-500">
                                                    Status: {district.is_active ? 'Active' : 'Inactive'}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleToggleStatus(district)}
                                                className={`flex items-center gap-2 px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                                    district.is_active
                                                        ? 'text-green-700 bg-green-100 hover:bg-green-200'
                                                        : 'text-red-700 bg-red-100 hover:bg-red-200'
                                                }`}
                                            >
                                                {district.is_active ? (
                                                    <>
                                                        <FaToggleOn className="w-4 h-4" />
                                                        Active
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaToggleOff className="w-4 h-4" />
                                                        Inactive
                                                    </>
                                                )}
                                            </button>

                                            <button
                                                onClick={() => handleOpenEdit(district)}
                                                disabled={editingDistrict || addingDistrict}
                                                className="p-2 text-gray-600 bg-gray-100 rounded hover:bg-blue-100 hover:text-blue-600 transition-colors disabled:opacity-50"
                                                title="Edit district"
                                            >
                                                <FaEdit className="w-4 h-4" />
                                            </button>

                                            <button
                                                onClick={() => handleOpenDelete(district.id)}
                                                disabled={deletingDistrict || addingDistrict}
                                                className="p-2 text-gray-600 bg-gray-100 rounded hover:bg-red-100 hover:text-red-600 transition-colors disabled:opacity-50"
                                                title="Delete district"
                                            >
                                                <FaTrash className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination && pagination.total_pages > 1 && (
                            <div className="flex items-center justify-between mt-6">
                                <div className="text-sm text-gray-700">
                                    Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total_count)} of {pagination.total_count} results
                                </div>
                                
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handlePageChange(pagination.prev_page!)}
                                        disabled={!pagination.has_prev_page}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    
                                    <span className="px-3 py-1 text-sm border border-gray-300 rounded  bg-blue-50">
                                        {pagination.page} / {pagination.total_pages}
                                    </span>
                                    
                                    <button
                                        onClick={() => handlePageChange(pagination.next_page!)}
                                        disabled={!pagination.has_next_page}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Delete Confirmation Modal */}
                        {showDeleteConfirm && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                                <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete District?</h3>
                                    <p className="text-sm text-gray-600 mb-6">
                                        Are you sure you want to delete this district? This action cannot be undone.
                                    </p>
                                    <div className="flex gap-3 justify-end">
                                        <button
                                            onClick={() => {
                                                setShowDeleteConfirm(false);
                                                setDeletingDistrictId(null);
                                            }}
                                            disabled={deletingDistrict}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleDeleteDistrict}
                                            disabled={deletingDistrict}
                                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {deletingDistrict ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                    Deleting...
                                                </>
                                            ) : (
                                                <>
                                                    <FaTrash className="w-4 h-4" />
                                                    Delete
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex justify-end">
                        <button
                            onClick={handleClose}
                            disabled={addingDistrict || editingDistrict || deletingDistrict}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
